/**
 * Lighting + shadow-catcher rig for the XTS scene.
 *
 * Two modes:
 *   - environmentLighting = true  → IBL via SceneEnvironment supplies most
 *     of the fill; ambient/hemisphere terms are dialled down so the scene
 *     doesn't over-brighten.
 *   - environmentLighting = false → no IBL; a brighter ambient + cool
 *     hemisphere fill keep shadow-side faces legible.
 *
 * The shadow-catcher plane is only mounted when `shadows` is true so the
 * scene graph stays minimal otherwise.
 */

import React from 'react';
import { SceneEnvironment } from './SceneEnvironment.js';

interface SceneLightingProps {
  shadows: boolean;
  environmentLighting: boolean;
  environmentIntensity: number;
}

export const SceneLighting: React.FC<SceneLightingProps> = ({
  shadows,
  environmentLighting,
  environmentIntensity,
}) => {
  // With IBL on, drop the flat ambient term so the scene doesn't over-brighten.
  // Without IBL, keep a brighter baseline so direct-light-only setups stay legible.
  const ambientIntensity = environmentLighting ? 0.25 : 0.6;
  const directionalIntensity = environmentLighting ? 1.1 : 0.8;

  return (
    <>
      {environmentLighting && (
        <SceneEnvironment intensity={environmentIntensity} />
      )}
      <ambientLight intensity={ambientIntensity} />
      {/* Hemisphere fill — cheap bounce-light approximation. Skipped when
       *  the IBL is on; it would double up. */}
      {!environmentLighting && (
        <hemisphereLight
          color="#cfdfee"
          groundColor="#454c52"
          intensity={0.35}
        />
      )}
      {/* Primary fill + shadow caster. Shadow camera sized for a typical
       *  XTS line (≈ 5 m square at z=0). */}
      <directionalLight
        position={[1500, 1500, 3000]}
        intensity={directionalIntensity}
        castShadow={shadows}
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-radius={6}
        shadow-blurSamples={16}
        shadow-camera-near={500}
        shadow-camera-far={8000}
        shadow-camera-left={-3500}
        shadow-camera-right={3500}
        shadow-camera-top={3500}
        shadow-camera-bottom={-3500}
        shadow-bias={-0.0005}
        shadow-normalBias={1.5}
      />
      {/* Cool rim light from the opposite side. Dimmed under IBL to avoid wash-out. */}
      <directionalLight
        position={[-1000, -1000, 1500]}
        intensity={environmentLighting ? 0.18 : 0.3}
        color="#dde7ff"
      />
      {/* Shadow catcher: invisible plane that only renders the shadow.
       *  Lives at -1 mm so module rails (z=0) never z-fight with it. */}
      {shadows && (
        <mesh position={[0, 0, -1]} receiveShadow renderOrder={-1}>
          <planeGeometry args={[20000, 20000]} />
          <shadowMaterial transparent opacity={0.32} />
        </mesh>
      )}
    </>
  );
};
